// ─────────────────────────────────────────────────────────────────────────────
// Mock Handlers — har API endpoint ko uski mock response se map karta hai
//
// Jab USE_MOCK = true hota hai, apiService.js yahan se response uthata hai
// aur real HTTP call nahi karta.
// ─────────────────────────────────────────────────────────────────────────────

import { API_PATHS } from '../constants/portalApiPath';

import {
  mockPatientListResponse,
  mockPatientDetails,
  mockPatientPaymentHistory,
} from './mockPatients';

import {
  mockEmployeeListResponse,
  mockEmployeeDetails,
  mockSalaryListResponse,
  mockAttendanceListResponse,
  mockStaffSessionResponse,
  mockMemberSessionRatesResponse,
  mockStaffActivitySummaryResponse,
  mockStaffAttendanceSummaryResponse,
} from './mockEmployees';

import {
  mockDashboardCounts,
  mockActivePatientsBySegment,
  mockUpcomingBirthdays,
  mockOverviewGraph,
  mockMembersGraph,
} from './mockDashboard';

import {
  mockAppointmentListHandler,
  mockCheckConflictsResponse,
  mockAvailableSlotsResponse,
  mockTransitionResponse,
  mockTherapistSchedulesResponse,
  mockTherapistScheduleUpdateResponse,
} from './mockAppointments';

import {
  mockInvoiceListResponse,
  mockInvoicePaymentHistory,
} from './mockInvoices';

import {
  mockIncomeListResponse,
  mockExpenseListResponse,
} from './mockIncomeExpense';

import {
  mockDashboardInsights,
  mockPricingInsights,
  mockOverdueInvoices,
} from './mockAnalytics';

import {
  mockPatientProgress,
  mockSinglePatientProgress,
  mockTherapyEffectiveness,
  mockGoalAchievement,
  mockSinglePatientGoals,
  mockStagnationAlerts,
  mockClinicalRecommendations,
  mockSinglePatientRecommendations,
} from './mockClinical';

import {
  mockServicesListResponse,
} from './mockServices';

import {
  mockSessionTypesListResponse,
} from './mockSessionTypes';

import {
  mockDesignationsListResponse,
  mockDesignationServicesResponse,
  mockDesignationDropdownResponse,
} from './mockDesignations';

import {
  mockRoleDropdownResponse,
} from './mockRoles';

import {
  mockClinicsList,
  mockClinicDetails,
  mockPlansList,
  mockSubscriptionsList,
  mockSaasAnalytics,
  mockCrossBranchComparison,
  mockCrossBranchRevenue,
  mockCrossBranchPerformance,
  mockPaymentCreateOrder,
  mockPaymentVerify,
  mockPaymentHistory,
  mockImpersonateClinic,
  mockOnboardingStatus,
  mockActivityLogs,
  mockUsageStats,
  mockPaymentFailures,
} from './mockSaas';

import {
  mockRoomListResponse,
} from './mockRooms';

import {
  mockWaitingListResponse,
  mockMatchingPatientsResponse,
  mockPatientWaitingEntries,
} from './mockWaitingList';

import {
  mockPaymentRemindersResponse,
  mockSendEmailResponse,
} from './mockPaymentReminders';

import {
  mockNotificationsListResponse,
  mockUnreadCountResponse,
  mockSmartAlertsResponse,
} from './mockNotifications';

import {
  mockPricingPlansList,
  mockPricingPlanCreate,
  mockPricingPlanToggle,
  mockPatientPlanGet,
  mockPatientPlanList,
  mockPatientPlanAssign,
  mockPatientPlanClose,
  mockAttendanceList,
  mockAttendanceMark,
  mockCentreOff,
  mockWalletBalance,
  mockWalletAddPayment,
  mockWalletRefund,
  mockWalletTransactions,
  mockSettlementPreview,
  mockSettlementGenerate,
  mockBillingInvoiceGet,
  mockBillingInvoicesList,
} from './mockBilling';

import {
  mockSalaryStructureResponse,
  mockPayrollListResponse,
  mockPayrollDetailsResponse,
  mockPayrollSummaryResponse,
  mockAdvanceListResponse,
  mockStaffOverviewResponse,
} from './mockPayroll';

import {
  mockGroupSessionList,
  mockGroupSessionDetails,
  mockAvailablePatients,
} from './mockGroupSessions';

import {
  mockSupportedDevices,
  mockDeviceListResponse,
  mockMappingsResponse,
} from './mockBiometric';

import {
  mockNewsletterSubscribers,
  mockTestimonials,
} from './mockContent';

// ─────────────────────────────────────────────────────────────────────────────
// Naya endpoint add karna ho to bas yahan ek line add karo:
//   [API_PATHS.YOUR_NEW_ENDPOINT]: yourMockResponse,
// ─────────────────────────────────────────────────────────────────────────────

export const mockHandlers = {
  // Dashboard
  [API_PATHS.DASHBOARD_COUNTS]:        mockDashboardCounts,
  [API_PATHS.DASHBOARD_ACTIVE_PATIENTS]: mockActivePatientsBySegment,
  [API_PATHS.DASHBOARD_BIRTHDAY]:      mockUpcomingBirthdays,
  [API_PATHS.DASHBOARD_OVERVIEW_GRAPH]: mockOverviewGraph,
  [API_PATHS.DASHBOARD_MEMBERS_GRAPH]: mockMembersGraph,

  // Patient
  [API_PATHS.PATIENT_LIST]:            mockPatientListResponse,
  [API_PATHS.PATIENT_ADD]:             { rescode: 1, message: 'Patient added successfully', data: {} },
  [API_PATHS.PATIENT_DETAILS]:         mockPatientDetails,
  [API_PATHS.PATIENT_PAYMENT_HISTORY]: mockPatientPaymentHistory,
  [API_PATHS.PATIENT_SESSION]:         { rescode: 1, message: 'Sessions fetched', data: { appointments: [
    { id: 401, session_name: "Speech Therapy", session_type: "Regular", date: "2026-03-07", start_time: "09:00", end_time: "09:45", is_absent: 0, is_complete: 0 },
    { id: 402, session_name: "Occupational Therapy", session_type: "MTS", date: "2026-03-05", start_time: "10:00", end_time: "10:45", is_absent: 0, is_complete: 1 },
    { id: 403, session_name: "Speech Therapy", session_type: "Regular", date: "2026-03-03", start_time: "09:00", end_time: "09:45", is_absent: 1, is_complete: 0 },
    { id: 404, session_name: "Special Education", session_type: "Regular", date: "2026-02-28", start_time: "11:00", end_time: "11:45", is_absent: 0, is_complete: 1 },
    { id: 405, session_name: "Speech Therapy", session_type: "Follow-Up", date: "2026-02-26", start_time: "09:00", end_time: "09:45", is_absent: 0, is_complete: 1 },
  ], recordCount: 5 } },
  [API_PATHS.PATIENT_DEACTIVATE]:      { rescode: 1, message: 'Patient deactivated. 3 future session(s) cancelled.', data: { cancelledSessions: 3 } },
  [API_PATHS.PRESCRIPTION_UPLOAD]:     { rescode: 1, message: 'Prescription uploaded', data: { prescription: { id: 1, file_name: 'mock.png', original_name: 'prescription.png', created_at: '2026-03-01' } } },
  [API_PATHS.PRESCRIPTION_LIST]:       { rescode: 1, data: { prescriptions: [], total: 0 } },
  [API_PATHS.PRESCRIPTION_DELETE]:     { rescode: 1, message: 'Prescription deleted' },

  // Staff / Employee
  [API_PATHS.STAFF_LIST]:              mockEmployeeListResponse,
  [API_PATHS.STAFF_LOGIN_OVERVIEW]:   { rescode: 1, data: {
    summary: { total: 4, active: 4, inactive: 0, loggedIn: 3, neverLoggedIn: 1 },
    users: [
      { UserId: 101, FirstName: 'Anita', LastName: 'Sharma', Email: 'anita@clinic.com', Phone: '9876543210', RoleId: 2, RoleName: 'Admin', IsActive: 1, Image: null, designation: 'Clinic Admin', loginCount: 142, lastLoginAt: '2026-06-07T08:15:00Z' },
      { UserId: 102, FirstName: 'Rahul', LastName: 'Verma', Email: 'rahul@clinic.com', Phone: '9876500011', RoleId: 5, RoleName: 'Therapist', IsActive: 1, Image: null, designation: 'Physiotherapist', loginCount: 47, lastLoginAt: '2026-06-06T17:42:00Z' },
      { UserId: 103, FirstName: 'Priya', LastName: 'Nair', Email: 'priya@clinic.com', Phone: '9876500022', RoleId: 5, RoleName: 'Therapist', IsActive: 1, Image: null, designation: 'Occupational Therapist', loginCount: 12, lastLoginAt: '2026-06-04T11:05:00Z' },
      { UserId: 104, FirstName: 'Sunil', LastName: 'Kumar', Email: 'sunil@clinic.com', Phone: '9876500033', RoleId: 5, RoleName: 'Receptionist', IsActive: 1, Image: null, designation: 'Front Desk', loginCount: 0, lastLoginAt: null },
    ],
  } },
  [API_PATHS.STAFF_ADD]:               { rescode: 1, message: 'Staff added successfully', data: {} },
  [API_PATHS.STAFF_DETAILS]:           mockEmployeeDetails,
  [API_PATHS.STAFF_UPDATE_PROFILE_IMAGE]: { rescode: 1, message: 'Profile image updated', data: {} },
  [API_PATHS.STAFF_ADD_SALARY]:        { rescode: 1, message: 'Salary added successfully', data: {} },
  [API_PATHS.STAFF_SALARY_LIST]:       mockSalaryListResponse,
  [API_PATHS.STAFF_ATTENDANCE_LIST]:   mockAttendanceListResponse,
  [API_PATHS.STAFF_SESSION]:           mockStaffSessionResponse,
  [API_PATHS.STAFF_ACTIVITY_SUMMARY]:  mockStaffActivitySummaryResponse,
  [API_PATHS.STAFF_ATTENDANCE_SUMMARY]: mockStaffAttendanceSummaryResponse,

  // Member Session Rates
  [API_PATHS.MEMBER_SESSION_RATES_LIST]:   mockMemberSessionRatesResponse,
  [API_PATHS.MEMBER_SESSION_RATES_SAVE]:   { rescode: 1, message: 'Session rates saved successfully', data: {} },
  [API_PATHS.MEMBER_SESSION_RATES_UPDATE]: { rescode: 1, message: 'Session rate updated successfully', data: {} },
  [API_PATHS.MEMBER_SESSION_RATES_DELETE]: { rescode: 1, message: 'Session rate deleted successfully', data: {} },

  // Attendance
  [API_PATHS.STAFF_ATTENDANCE]:        { rescode: 1, message: 'Attendance marked successfully', data: {} },
  [API_PATHS.STAFF_ATTENDANCE_REQUEST_APPROVE]: { rescode: 1, message: 'Attendance change approved.', data: {} },
  [API_PATHS.STAFF_ATTENDANCE_REQUEST_REJECT]:  { rescode: 1, message: 'Attendance change rejected.', data: {} },

  // Appointment
  [API_PATHS.APPOINTMENT]:             mockAppointmentListHandler,
  [API_PATHS.APPOINTMENT_ADD]:         { rescode: 1, message: 'Appointment booked successfully', data: { invoice_id: 1006 } },
  [API_PATHS.APPOINTMENT_MARK_STATUS]: { rescode: 1, message: 'Appointment status updated successfully' },
  [API_PATHS.APPOINTMENT_CANCEL]:      { rescode: 1, message: 'Appointment cancelled successfully' },
  [API_PATHS.APPOINTMENT_CHECK_CONFLICTS]: mockCheckConflictsResponse,
  [API_PATHS.APPOINTMENT_AVAILABLE_SLOTS]: mockAvailableSlotsResponse,
  [API_PATHS.APPOINTMENT_TRANSITION]:      mockTransitionResponse,
  [API_PATHS.APPOINTMENT_THERAPIST_SCHEDULES]:        mockTherapistSchedulesResponse,
  [API_PATHS.APPOINTMENT_THERAPIST_SCHEDULES_UPDATE]: mockTherapistScheduleUpdateResponse,
  // Moving and cancelling sessions — the demo never has anything already collected to
  // decide about, nor a therapist on another rate.
  [API_PATHS.APPOINTMENT_RESCHEDULE]: { rescode: 1, message: 'Session rescheduled.', data: { warnings: [], movedCount: 1, skipped: [], seriesSiblings: 0, rateChange: null, changed: true } },
  [API_PATHS.APPOINTMENT_RESCHEDULE_RATE_CHECK]: { rescode: 1, data: { differs: false, sessions: 0, fromRates: [], toRate: null, chargeAmount: 0, chargePayable: 0, creditAmount: 0 } },
  [API_PATHS.APPOINTMENT_CANCEL_CHECK]: { rescode: 1, data: { excessAmount: 0, totalExcessAmount: 0, rateAdjustmentExcess: 0, fee: 0, totalPaid: 0, currentTotal: 0 } },
  [API_PATHS.APPOINTMENT_CANCEL_SERIES_CHECK]: { rescode: 1, data: { excessAmount: 0, totalExcessAmount: 0, rateAdjustmentExcess: 0, toCancelFee: 0, renderedFee: 0, totalPaid: 0, remainingSeriesCount: 1, toCancelIds: [], elapsedCount: 0, elapsedFee: 0, elapsedIds: [] } },
  [API_PATHS.APPOINTMENT_CANCEL_SERIES]: { rescode: 1, message: 'Series cancelled.', data: { result: { cancelledCount: 1, invoiceDeleted: false, invoiceStatus: 'ACTIVE', elapsedCount: 0, elapsedFee: 0 } } },
  // Google Meet — demo appointments never have one, and Google is never reached from the demo.
  [API_PATHS.APPOINTMENT_GOOGLE_MEET]: (data) => ({
    rescode: 1,
    message: 'Success result',
    data: {
      appointments: (Array.isArray(data?.appointment_ids) ? data.appointment_ids : []).map((id) => ({
        appointment_id: Number(id), google_meet_enabled: false, state: 'NONE', meet_url: null, last_error: null, last_synced_at: null,
      })),
    },
  }),
  [API_PATHS.APPOINTMENT_GOOGLE_MEET_ENABLE]: { rescode: 0, type: 'demo', message: 'Demo mode: this action is disabled.', is_demo: true, data: {} },
  [API_PATHS.APPOINTMENT_GOOGLE_MEET_DISABLE]: { rescode: 0, type: 'demo', message: 'Demo mode: this action is disabled.', is_demo: true, data: {} },
  [API_PATHS.APPOINTMENT_GOOGLE_MEET_RETRY]: { rescode: 0, type: 'demo', message: 'Demo mode: this action is disabled.', is_demo: true, data: {} },
  // A departing staff member's sessions — the demo staff have nothing booked to move.
  [API_PATHS.STAFF_MEMBER_UPCOMING_SESSIONS]: { rescode: 1, data: { sessions: [], candidates: [{ id: 102, name: 'Rahul Verma' }, { id: 103, name: 'Priya Nair' }] } },
  [API_PATHS.STAFF_MEMBER_REASSIGN_SESSIONS]: { rescode: 1, message: '0 session(s) moved.', data: { moved: [], failed: [], renewed: 0 } },

  // Income
  [API_PATHS.INCOME_LIST]:             mockIncomeListResponse,
  [API_PATHS.INCOME_ADD]:              { rescode: 1, message: 'Income saved successfully', data: {} },

  // Expense
  [API_PATHS.EXPENSE_LIST]:            mockExpenseListResponse,
  [API_PATHS.EXPENSE_ADD]:             { rescode: 1, message: 'Expense saved successfully', data: {} },

  // Invoice
  [API_PATHS.INVOICE_LIST]:            mockInvoiceListResponse,
  [API_PATHS.INVOICE_PAYMENT_HISTORY]: mockInvoicePaymentHistory,

  // Payment
  [API_PATHS.SESSION_DISCOUNT_ADD_REMOVE]: { rescode: 1, message: 'Discount applied successfully', data: {} },
  [API_PATHS.ADD_INVOICE_PAYMENT]:         { rescode: 1, message: 'Payment recorded successfully', data: {} },

  // Analytics & Smart Insights
  [API_PATHS.DASHBOARD_INSIGHTS]:  mockDashboardInsights,
  [API_PATHS.ANALYTICS_PRICING]:   mockPricingInsights,
  [API_PATHS.INVOICES_OVERDUE]:    mockOverdueInvoices,

  // Clinical Intelligence (Phase 4)
  [API_PATHS.CLINICAL_PROGRESS]:                mockPatientProgress,
  [API_PATHS.CLINICAL_PROGRESS_SINGLE]:         mockSinglePatientProgress,
  [API_PATHS.CLINICAL_THERAPY_EFFECTIVENESS]:   mockTherapyEffectiveness,
  [API_PATHS.CLINICAL_GOALS]:                   mockGoalAchievement,
  [API_PATHS.CLINICAL_GOALS_SINGLE]:            mockSinglePatientGoals,
  [API_PATHS.CLINICAL_STAGNATION]:              mockStagnationAlerts,
  [API_PATHS.CLINICAL_RECOMMENDATIONS]:         mockClinicalRecommendations,
  [API_PATHS.CLINICAL_RECOMMENDATIONS_SINGLE]:  mockSinglePatientRecommendations,

  // SaaS Management (Phase 5)
  [API_PATHS.SAAS_CLINICS]:                mockClinicsList,
  [API_PATHS.SAAS_CLINIC_DETAILS]:         mockClinicDetails,
  [API_PATHS.SAAS_CLINIC_CREATE]:          { rescode: 1, message: 'Clinic created successfully', data: {} },
  [API_PATHS.SAAS_CLINIC_STATUS]:          { rescode: 1, message: 'Clinic status updated', data: {} },
  [API_PATHS.SAAS_CLINIC_IMPERSONATE]:     mockImpersonateClinic,
  [API_PATHS.SAAS_PLANS]:                  mockPlansList,
  [API_PATHS.SAAS_PLAN_CREATE]:            { rescode: 1, message: 'Plan created successfully', data: {} },
  [API_PATHS.SAAS_PLAN_UPDATE]:            { rescode: 1, message: 'Plan updated successfully', data: {} },
  [API_PATHS.SAAS_SUBSCRIPTIONS]:          mockSubscriptionsList,
  [API_PATHS.SAAS_SUBSCRIPTION_ASSIGN]:    { rescode: 1, message: 'Subscription assigned', data: {} },
  [API_PATHS.SAAS_ANALYTICS]:             mockSaasAnalytics,
  [API_PATHS.SAAS_ONBOARDING]:            mockOnboardingStatus,
  [API_PATHS.SAAS_ACTIVITY_LOGS]:         mockActivityLogs,
  [API_PATHS.SAAS_USAGE_STATS]:           mockUsageStats,
  [API_PATHS.SAAS_PAYMENT_FAILURES]:      mockPaymentFailures,
  [API_PATHS.SAAS_BRANDING_UPDATE]:        { rescode: 1, message: 'Branding updated', data: {} },

  // Cross-Branch Intelligence (Phase 5)
  [API_PATHS.CROSS_BRANCH_COMPARISON]:     mockCrossBranchComparison,
  [API_PATHS.CROSS_BRANCH_REVENUE]:        mockCrossBranchRevenue,
  [API_PATHS.CROSS_BRANCH_PERFORMANCE]:    mockCrossBranchPerformance,

  // Marketing Content — Newsletter & Testimonials
  [API_PATHS.NEWSLETTER_SUBSCRIBERS]:      mockNewsletterSubscribers,
  [API_PATHS.TESTIMONIALS_LIST]:           mockTestimonials,
  [API_PATHS.TESTIMONIALS_CREATE]:         { rescode: 1, message: 'Testimonial created successfully', data: {} },
  [API_PATHS.TESTIMONIALS_UPDATE]:         { rescode: 1, message: 'Testimonial updated successfully', data: {} },
  [API_PATHS.TESTIMONIALS_DELETE]:         { rescode: 1, message: 'Testimonial deleted successfully', data: {} },

  // Payment Gateway (Phase 6)
  [API_PATHS.SAAS_PAYMENT_CREATE_ORDER]:   mockPaymentCreateOrder,
  [API_PATHS.SAAS_PAYMENT_VERIFY]:         mockPaymentVerify,
  [API_PATHS.SAAS_PAYMENT_HISTORY]:        mockPaymentHistory,

  // Services Management
  [API_PATHS.SERVICES_CLINIC_TYPE]: { rescode: 1, data: { clinic_type: 'child_development:speech,ot,aba,special_education,physio_child,feeding,parent_counseling,assessment' } },
  [API_PATHS.SERVICES_CLINIC_TYPE_UPDATE]: { rescode: 1, data: { clinic_type: 'child_development', message: 'Clinic type updated successfully' } },
  [API_PATHS.SERVICES_REPLACE]: { rescode: 1, data: { services: [], message: 'Services replaced successfully' } },
  [API_PATHS.SERVICES_LIST]:    mockServicesListResponse,
  [API_PATHS.SERVICES_CREATE]:  { rescode: 1, message: 'Service created successfully', data: {} },
  [API_PATHS.SERVICES_UPDATE]:  { rescode: 1, message: 'Service updated successfully', data: {} },
  [API_PATHS.SERVICES_DELETE]:  { rescode: 1, message: 'Service deleted successfully', data: {} },
  [API_PATHS.SERVICES_TOGGLE]:  { rescode: 1, message: 'Service status toggled', data: {} },
  [API_PATHS.SERVICES_DUPLICATE]: { rescode: 1, message: 'Service duplicated', data: { service: { id: 99 } } },
  [API_PATHS.SERVICES_CATALOG]: { rescode: 1, data: {
    clinic_types: [{ value: 'child_development', label: 'Child Development / Therapy Centre' }, { value: 'general', label: 'General Clinic' }, { value: 'dental', label: 'Dental Clinic' }],
    services: [
      { name: 'Occupational Therapy', category: 'therapy', session_mode: 'individual', price: 1000, duration_minutes: 60, color: '#2563EB', description: 'Improve sensory, motor and daily living skills', already_exists: false },
      { name: 'Speech Therapy', category: 'therapy', session_mode: 'individual', price: 900, duration_minutes: 45, color: '#7C3AED', description: 'Develop communication and speech skills', already_exists: true },
      { name: 'Initial Assessment', category: 'assessment', session_mode: 'individual', price: 1500, duration_minutes: 60, color: '#F97316', description: 'Comprehensive initial evaluation', already_exists: false },
      { name: 'Parent Training', category: 'consultation', session_mode: 'individual', price: 500, duration_minutes: 45, color: '#F59E0B', description: 'Guidance for parents', already_exists: false },
    ],
  } },
  [API_PATHS.SERVICES_IMPORT_DEFAULTS]: { rescode: 1, data: { imported: 3, skipped: 1, message: '3 services imported, 1 skipped (already exist)' } },

  // Rooms Management
  [API_PATHS.ROOMS_LIST]:    mockRoomListResponse,
  [API_PATHS.ROOMS_CREATE]:  { rescode: 1, message: 'Room created successfully', data: {} },
  [API_PATHS.ROOMS_UPDATE]:  { rescode: 1, message: 'Room updated successfully', data: {} },
  [API_PATHS.ROOMS_DELETE]:  { rescode: 1, message: 'Room deleted successfully', data: {} },
  [API_PATHS.ROOMS_TOGGLE]:  { rescode: 1, message: 'Room status toggled', data: {} },

  // Session Types Management
  [API_PATHS.SESSION_TYPES_LIST]:    mockSessionTypesListResponse,
  [API_PATHS.SESSION_TYPES_CREATE]:  { rescode: 1, message: 'Session type created successfully', data: {} },
  [API_PATHS.SESSION_TYPES_UPDATE]:  { rescode: 1, message: 'Session type updated successfully', data: {} },
  [API_PATHS.SESSION_TYPES_DELETE]:  { rescode: 1, message: 'Session type deleted successfully', data: {} },
  [API_PATHS.SESSION_TYPES_TOGGLE]:  { rescode: 1, message: 'Session type status toggled', data: {} },
  // Who is booked on a type (SessionTypeBookingsDialog): nobody, no records — the demo then
  // reaches Yes, Delete and its demo-blocked popup, as before.
  [API_PATHS.SESSION_TYPES_USAGE]:   { rescode: 1, data: { usage: { today: '', booked: [], bookedSessions: 0, bookedPatients: 0, renewing: [], isBooked: false, history: { past_sessions: 0, cancelled_sessions: 0, payments: 0, quota_records: 0, payment_reminders: 0, waiting_list: 0, group_sessions: 0, billing_plans: 0 }, inHistory: false }, is_active: 1, is_default: false, is_group: false } },

  // Designations Management
  [API_PATHS.DESIGNATIONS_LIST]:    mockDesignationsListResponse,
  [API_PATHS.DESIGNATIONS_CREATE]:  { rescode: 1, message: 'Designation created successfully', data: {} },
  [API_PATHS.DESIGNATIONS_UPDATE]:  { rescode: 1, message: 'Designation updated successfully', data: {} },
  [API_PATHS.DESIGNATIONS_DELETE]:  { rescode: 1, message: 'Designation deleted successfully', data: {} },
  [API_PATHS.DESIGNATIONS_TOGGLE]:  { rescode: 1, message: 'Designation status toggled', data: {} },
  [API_PATHS.DESIGNATIONS_SERVICES]:     mockDesignationServicesResponse,
  [API_PATHS.DESIGNATIONS_SERVICES_SET]: { rescode: 1, message: 'Services assigned successfully', data: {} },
  [API_PATHS.DESIGNATIONS_DROPDOWN]:     mockDesignationDropdownResponse,

  // Role Dropdown
  [API_PATHS.ROLE_LIST_DROPDOWN]:        mockRoleDropdownResponse,

  // Patient Billing Engine (Phase 7)
  [API_PATHS.BILLING_PLANS_LIST]:           mockPricingPlansList,
  [API_PATHS.BILLING_PLANS_GET]:            mockPricingPlansList,
  [API_PATHS.BILLING_PLANS_CREATE]:         mockPricingPlanCreate,
  [API_PATHS.BILLING_PLANS_UPDATE]:         { rescode: 1, message: 'Plan updated successfully', data: {} },
  [API_PATHS.BILLING_PLANS_TOGGLE]:         mockPricingPlanToggle,
  [API_PATHS.BILLING_PATIENT_PLAN_ASSIGN]:  mockPatientPlanAssign,
  [API_PATHS.BILLING_PATIENT_PLAN_CLOSE]:   mockPatientPlanClose,
  [API_PATHS.BILLING_PATIENT_PLAN_GET]:     mockPatientPlanGet,
  [API_PATHS.BILLING_PATIENT_PLAN_LIST]:    mockPatientPlanList,
  [API_PATHS.BILLING_ATTENDANCE_MARK]:      mockAttendanceMark,
  [API_PATHS.BILLING_ATTENDANCE_BULK_MARK]: { rescode: 1, message: 'Bulk attendance marked', data: [] },
  [API_PATHS.BILLING_ATTENDANCE_CENTRE_OFF]: mockCentreOff,
  [API_PATHS.BILLING_ATTENDANCE_LIST]:      mockAttendanceList,
  [API_PATHS.BILLING_WALLET_BALANCE]:       mockWalletBalance,
  [API_PATHS.BILLING_WALLET_ADD_PAYMENT]:   mockWalletAddPayment,
  [API_PATHS.BILLING_WALLET_REFUND]:        mockWalletRefund,
  [API_PATHS.BILLING_WALLET_TRANSACTIONS]:  mockWalletTransactions,
  [API_PATHS.BILLING_WALLET_MONTH_CREDITS]: (data) => ({ rescode: 1, data: { month: data?.month, label: data?.month, entries: [] } }),
  [API_PATHS.BILLING_WALLET_PAY_INVOICES]:  { rescode: 1, message: 'Paid from the wallet', data: { paid: 0, allocations: [] } },
  [API_PATHS.BILLING_WALLET_ADD_CREDIT]: { rescode: 1, data: { message: 'Added to the wallet.' } },
  [API_PATHS.BILLING_WALLET_CREDIT_REQUEST_CREATE]: { rescode: 1, data: { message: 'Request sent to the Admin.' } },
  [API_PATHS.BILLING_WALLET_CREDIT_REQUEST_LIST]: { rescode: 1, data: { requests: [] } },
  [API_PATHS.BILLING_WALLET_CREDIT_REQUEST_DECIDE]: { rescode: 1, data: { message: 'Done.' } },
  [API_PATHS.BILLING_WALLET_SETTLEMENT_LIST]: (data) => ({ rescode: 1, data: { month: data?.month, label: data?.month, month_over: true, rows: [] } }),
  [API_PATHS.BILLING_WALLET_SETTLEMENT_SET_AMOUNT]: { rescode: 1, message: 'Wallet credit updated.', data: {} },
  [API_PATHS.BILLING_WALLET_SETTLEMENT_APPLY_DIFFERENCE]: { rescode: 1, message: 'Difference applied to the wallet.', data: {} },
  [API_PATHS.BILLING_WALLET_SETTLEMENT_PATIENT_MONTH]: (data) => ({ rescode: 1, data: { month: data?.month, label: data?.month, month_over: true, rows: [] } }),
  [API_PATHS.BILLING_ATTENDANCE_UNMARKED]: { rescode: 1, data: { days: [] } },
  [API_PATHS.BILLING_SETTLEMENT_PREVIEW]:   mockSettlementPreview,
  [API_PATHS.BILLING_SETTLEMENT_GENERATE]:  mockSettlementGenerate,
  [API_PATHS.BILLING_INVOICES_GET]:         mockBillingInvoiceGet,
  [API_PATHS.BILLING_INVOICES_LIST]:        mockBillingInvoicesList,
  [API_PATHS.BILLING_PATIENT_PREVIEW]:     { rescode: 1, message: 'Billing preview fetched', data: {
    has_plan: true,
    plan_name: 'Monthly Speech Therapy (Min 24)',
    billing_model: 'MONTHLY_WITH_MINIMUM',
    monthly_price: 15000,
    minimum_visits: 24,
    total_visits: null,
    visits_used: 0,
    remaining_visits: null,
    period: { start: '2026-03-01', end: '2026-03-31' },
    working_days: 26,
    present_days: 18,
    patient_off_days: 2,
    centre_off_days: 3,
    estimated_amount: 15000,
    wallet_balance: 5000,
    breakdown: {
      formula: 'Full monthly price (billable >= effective minimum)',
      monthly_price: 15000,
      minimum_visits: 24,
      effective_minimum: 24,
      billable_days: 20,
      result: 15000,
    },
  }},

  // Clinic Settings (stateful — updates persist in memory until page hard-refresh)
  [API_PATHS.SETTINGS_GET]: { rescode: 1, message: 'Settings fetched', data: { settings: { appointment_settings: '{"start_time":"09:00","end_time":"18:00","slot_duration":45,"lunch_start":"13:30","lunch_end":"14:15"}' } } },
  [API_PATHS.SETTINGS_UPDATE]: { rescode: 1, message: 'Settings updated successfully', data: {} },
  [API_PATHS.SETTINGS_CLINIC_DETAILS]: { rescode: 1, message: 'Clinic details fetched', data: { clinic: { id: 1, name: 'Vistaar Clinic', address: '123 Main St', phone: '9876543210', email: 'info@vistaar.com' } } },
  [API_PATHS.SETTINGS_CLINIC_UPDATE]: { rescode: 1, message: 'Clinic details updated successfully', data: {} },

  // Google Calendar & Meet — as the backend answers a demo user: nothing connected, nothing to manage.
  [API_PATHS.GOOGLE_INTEGRATION_STATUS]: { rescode: 1, message: 'Success result', data: { enabled: true, available: true, status: 'NOT_CONNECTED', connected: false, needs_reconnect: false, can_manage: false } },
  [API_PATHS.GOOGLE_INTEGRATION_CONNECT]: { rescode: 0, type: 'demo', message: 'Demo mode: this action is disabled.', is_demo: true, data: {} },
  [API_PATHS.GOOGLE_INTEGRATION_DISCONNECT]: { rescode: 0, type: 'demo', message: 'Demo mode: this action is disabled.', is_demo: true, data: {} },

  // Waiting List
  [API_PATHS.WAITING_LIST]:            mockWaitingListResponse,
  [API_PATHS.WAITING_LIST_ADD]:        { rescode: 1, message: 'Patient added to waiting list', data: {} },
  [API_PATHS.WAITING_LIST_UPDATE]:     { rescode: 1, message: 'Waiting list entry updated', data: {} },
  [API_PATHS.WAITING_LIST_ASSIGN]:     { rescode: 1, message: 'Slot assigned successfully', data: {} },
  [API_PATHS.WAITING_LIST_CANCEL]:     { rescode: 1, message: 'Waiting list entry cancelled', data: {} },
  [API_PATHS.WAITING_LIST_MATCHING]:   mockMatchingPatientsResponse,
  [API_PATHS.WAITING_LIST_PATIENT]:    mockPatientWaitingEntries,
  [API_PATHS.WAITING_LIST_UNASSIGNED_PATIENTS]: { rescode: 1, data: { patients: [
    { UserId: 17, FirstName: "Riya", LastName: "Patel", Phone: "9876000001" },
    { UserId: 18, FirstName: "Arjun", LastName: "Mehta", Phone: "9876000002" },
    { UserId: 19, FirstName: "Sneha", LastName: "Gupta", Phone: "9876000003" },
  ], total: 3 } },

  // Payment Reminders
  [API_PATHS.PAYMENT_REMINDERS_LIST]:       mockPaymentRemindersResponse,
  [API_PATHS.PAYMENT_REMINDERS_SEND_EMAIL]: mockSendEmailResponse,

  // Notifications
  [API_PATHS.NOTIFICATIONS_LIST]:           mockNotificationsListResponse,
  [API_PATHS.NOTIFICATIONS_UNREAD_COUNT]:   mockUnreadCountResponse,
  [API_PATHS.NOTIFICATIONS_MARK_READ]:      { rescode: 1, message: 'Notification marked as read', data: {} },
  [API_PATHS.NOTIFICATIONS_MARK_ALL_READ]:  { rescode: 1, message: 'All notifications marked as read', data: {} },
  [API_PATHS.NOTIFICATIONS_DELETE]:         { rescode: 1, message: 'Notification deleted', data: {} },
  [API_PATHS.NOTIFICATIONS_SMART_ALERTS]:   mockSmartAlertsResponse,

  // Group Sessions
  [API_PATHS.GROUP_SESSION_LIST]:               mockGroupSessionList,
  [API_PATHS.GROUP_SESSION_DETAILS]:            mockGroupSessionDetails,
  [API_PATHS.GROUP_SESSION_SAVE]:               { rescode: 1, message: 'Group session saved', data: { groupId: 1 } },
  [API_PATHS.GROUP_SESSION_DELETE]:             { rescode: 1, message: 'Group session deleted', data: {} },
  [API_PATHS.GROUP_SESSION_TOGGLE]:             { rescode: 1, message: 'Status toggled', data: {} },
  [API_PATHS.GROUP_SESSION_AVAILABLE_PATIENTS]: mockAvailablePatients,
  [API_PATHS.GROUP_SESSION_VALIDATE_CONFLICTS]: { rescode: 1, data: { isValid: true, errors: [], warnings: [] } },
  [API_PATHS.GROUP_SESSION_ADD_PATIENT]:        { rescode: 1, message: 'Patient added', data: {} },
  [API_PATHS.GROUP_SESSION_REMOVE_PATIENT]:     { rescode: 1, message: 'Patient removed', data: {} },

  // Biometric Device Management
  [API_PATHS.BIOMETRIC_SUPPORTED_DEVICES]: mockSupportedDevices,
  [API_PATHS.BIOMETRIC_DEVICE_LIST]:       mockDeviceListResponse,
  [API_PATHS.BIOMETRIC_DEVICE_SAVE]:       { rescode: 1, message: 'Device saved', data: {} },
  [API_PATHS.BIOMETRIC_DEVICE_DELETE]:     { rescode: 1, message: 'Device deleted', data: {} },
  [API_PATHS.BIOMETRIC_DEVICE_TEST]:       { rescode: 1, data: { success: true, info: { serialNumber: 'ZK123456' } } },
  [API_PATHS.BIOMETRIC_DEVICE_SYNC]:       { rescode: 1, data: { message: 'Synced 8 records', imported: 8, errors: [] } },
  [API_PATHS.BIOMETRIC_DEVICE_FETCH_USERS]: { rescode: 1, data: { message: 'Found 5 users', users: [{ machineUserId: '1', machineUserName: 'User 1' }, { machineUserId: '2', machineUserName: 'User 2' }] } },
  [API_PATHS.BIOMETRIC_MAPPINGS_LIST]:     mockMappingsResponse,
  [API_PATHS.BIOMETRIC_MAPPINGS_SAVE]:     { rescode: 1, message: 'Mapping saved', data: {} },

  // CSV Attendance Import
  [API_PATHS.ATTENDANCE_PREVIEW_CSV]: { rescode: 1, data: { headers: ['UserId', 'Date', 'SignIn', 'SignOut', 'IsPresent'], sampleRows: [['201', '2026-03-01', '09:05:00', '18:10:00', 'Yes'], ['201', '2026-03-02', '09:15:00', '17:45:00', 'Yes']], totalRows: 5, autoMapping: { userId: 0, date: 1, signIn: 2, signOut: 3, isPresent: 4 } } },
  [API_PATHS.ATTENDANCE_IMPORT_CSV]: { rescode: 1, data: { message: 'Import complete. Imported: 5, Failed: 0', imported: 5, totalParsed: 5, failed: 0, errors: [] } },

  // Support Tickets
  [API_PATHS.SUPPORT_CREATE]: { rescode: 1, data: { ticket: { id: 1 }, message: 'Support ticket created successfully.' } },
  [API_PATHS.SUPPORT_LIST]: { rescode: 1, data: { tickets: [
    { id: 1, subject: "Cannot generate invoice", message: "When I try to generate monthly invoice, it shows error. Please help.", status: "open", priority: "high", user_id: 10, clinic_id: 1, admin_reply: null, replied_at: null, created_at: "2026-04-01T10:30:00", user: { UserId: 10, FirstName: "Jay", LastName: "Sharma", Email: "admin@gmail.com" }, clinic: { id: 1, name: "Vistaar Care" } },
    { id: 2, subject: "Need help with patient import", message: "How do I import patients from CSV file?", status: "resolved", priority: "normal", user_id: 10, clinic_id: 1, admin_reply: "You can import patients from Settings > Import. Upload CSV with columns: Name, Phone, Email.", replied_at: "2026-03-29T14:00:00", created_at: "2026-03-28T09:15:00", user: { UserId: 10, FirstName: "Jay", LastName: "Sharma", Email: "admin@gmail.com" }, clinic: { id: 1, name: "Vistaar Care" } },
  ], total: 2 } },
  [API_PATHS.SUPPORT_REPLY]: { rescode: 1, data: { ticket: { id: 1 }, message: 'Reply sent successfully.' } },
  [API_PATHS.SUPPORT_STATUS]: { rescode: 1, data: { ticket: { id: 1 } } },

  // Payroll Management
  [API_PATHS.PAYROLL_SALARY_STRUCTURE_SAVE]: { rescode: 1, message: 'Salary structure saved successfully', data: {} },
  [API_PATHS.PAYROLL_SALARY_STRUCTURE_GET]:  mockSalaryStructureResponse,
  [API_PATHS.PAYROLL_GENERATE]:              { rescode: 1, message: 'Payroll generated successfully', data: {} },
  [API_PATHS.PAYROLL_BULK_GENERATE]:         { rescode: 1, message: 'Bulk payroll generated', data: { generated: 3, skipped: 0, failed: 0 } },
  [API_PATHS.PAYROLL_UPDATE_STATUS]:         { rescode: 1, message: 'Payroll status updated', data: {} },
  [API_PATHS.PAYROLL_LIST]:                  mockPayrollListResponse,
  [API_PATHS.PAYROLL_DETAILS]:               mockPayrollDetailsResponse,
  [API_PATHS.PAYROLL_SUMMARY]:               mockPayrollSummaryResponse,
  [API_PATHS.PAYROLL_DELETE]:                { rescode: 1, message: 'Payroll deleted', data: {} },
  [API_PATHS.PAYROLL_ADVANCE_SAVE]:          { rescode: 1, message: 'Advance saved successfully', data: {} },
  [API_PATHS.PAYROLL_ADVANCE_LIST]:          mockAdvanceListResponse,
  [API_PATHS.PAYROLL_STAFF_OVERVIEW]:        mockStaffOverviewResponse,

  // Revenue by Service Analytics
  [API_PATHS.ANALYTICS_REVENUE_BY_SERVICE]: { rescode: 1, data: {
    services: [
      { service_id: 1, service_name: 'Occupational Therapy', color: '#3b82f6', total_sessions: 84, total_revenue: 84000, avg_fee: 1000, revenue_pct: 38, therapists: [{ therapist_id: 3, therapist_name: 'Priya', sessions: 50, revenue: 50000 }, { therapist_id: 7, therapist_name: 'Neha', sessions: 34, revenue: 34000 }] },
      { service_id: 2, service_name: 'Speech Therapy', color: '#8b5cf6', total_sessions: 62, total_revenue: 55800, avg_fee: 900, revenue_pct: 25, therapists: [{ therapist_id: 4, therapist_name: 'Rahul', sessions: 62, revenue: 55800 }] },
      { service_id: 3, service_name: 'Special Education', color: '#f59e0b', total_sessions: 48, total_revenue: 43200, avg_fee: 900, revenue_pct: 20, therapists: [{ therapist_id: 5, therapist_name: 'Anita', sessions: 48, revenue: 43200 }] },
      { service_id: 4, service_name: 'Physiotherapy', color: '#10b981', total_sessions: 36, total_revenue: 39600, avg_fee: 1100, revenue_pct: 17, therapists: [{ therapist_id: 6, therapist_name: 'Vikram', sessions: 36, revenue: 39600 }] },
    ],
    total_revenue: 222600,
    total_sessions: 230,
  } },
};
